import { OffsetMap } from '../types/offsetMap'
import { buildConnectivityGraph, isFrameConnected } from './offsetMapUtils'
import { getCumulativeOffset, calculateFrameOffsets, getFrameRange } from './offsetCalculations'

/**
 * Result of visible frame calculation
 */
export interface VisibleFramesResult {
  indices: number[]                          // Array of visible frame indices
  offsets: Map<number, { x: number; y: number }> // Frame index → offset from current frame
}

/**
 * Calculate which frames should be visible using a simple range approach.
 * 
 * Phase 1: Simple implementation that shows ±N frames around current frame.
 * Does not consider viewport coverage, just a fixed range.
 * 
 * @param currentFrame - Reference frame index
 * @param range - Number of frames before and after (2 = ±2 = 5 frames total)
 * @param totalFrames - Total number of frames available
 * @param offsetMap - Pre-calculated offset map
 * @returns Visible frame indices and their offsets relative to current frame
 */
export function calculateVisibleFramesSimple(
  currentFrame: number,
  range: number,
  totalFrames: number,
  offsetMap: OffsetMap | null
): VisibleFramesResult {
  // Get frame indices in range
  const frameIndices = getFrameRange(currentFrame, range, totalFrames)

  // Calculate offsets for each frame
  const offsets = calculateFrameOffsets(currentFrame, frameIndices, offsetMap)

  // Filter to only include frames with valid offsets
  const validIndices = frameIndices.filter(index => offsets.has(index))

  return {
    indices: validIndices,
    offsets
  }
}

/**
 * Calculate which frames are visible in the viewport based on coverage.
 * 
 * Phase 2: Advanced implementation that checks actual viewport intersection.
 * Only shows frames that have >threshold% coverage of the viewport.
 * 
 * @param currentFrame - Reference frame index
 * @param panOffset - Current pan offset {x, y}
 * @param viewportWidth - Viewport width in pixels
 * @param viewportHeight - Viewport height in pixels
 * @param imageWidth - Frame image width in pixels
 * @param imageHeight - Frame image height in pixels
 * @param offsetMap - Pre-calculated offset map
 * @param options - Optional configuration
 * @returns Visible frame indices, offsets, and coverage percentages
 */
export function calculateVisibleFramesInViewport(
  currentFrame: number,
  panOffset: { x: number; y: number },
  viewportWidth: number,
  viewportHeight: number,
  imageWidth: number,
  imageHeight: number,
  offsetMap: OffsetMap | null,
  options?: {
    maxFrames?: number      // Maximum number of frames to show (default: 21 = ±10)
    coverageThreshold?: number // Minimum coverage % to show (default: 0.01 = 1%)
    searchRange?: number    // How far to search for frames (default: 15 = ±15)
  }
): VisibleFramesResult & { coverages: Map<number, number> } {
  const maxFrames = options?.maxFrames ?? 21
  const coverageThreshold = options?.coverageThreshold ?? 0.01
  const searchRange = options?.searchRange ?? 15

  console.log(
    `[calculateVisibleFramesInViewport] Current: ${currentFrame}, ` +
    `Search: ±${searchRange}, Max: ${maxFrames}, Threshold: ${(coverageThreshold * 100).toFixed(0)}%`
  )

  // TODO Phase 2: Implement viewport-based detection
  // For now, return empty result (will be implemented in Phase 2)
  
  console.warn('[calculateVisibleFramesInViewport] Not yet implemented (Phase 2)')
  
  return {
    indices: [currentFrame],
    offsets: new Map([[currentFrame, { x: 0, y: 0 }]]),
    coverages: new Map([[currentFrame, 1.0]])
  }
}

/**
 * Phase 4: Calculate visible frames around a reference point.
 *
 * Holy Red Dot Paradigm: This function finds frames within proximity of a reference point
 * (typically the world position under the red dot at screen center). Frames are positioned
 * relative to this reference point to provide proper spatial context for navigation.
 *
 * @param referenceWorldPosition - Reference point in world space (e.g., world under red dot)
 * @param viewportSize - Viewport dimensions (used for radius calculation)
 * @param frameWorldPositions - Positions of all frames in world space
 * @param frameSize - Size of each frame {width, height}
 * @param options - Optional configuration
 * @returns Visible frame indices, render offsets, and proximity scores
 */
export interface FrameWorldPosition {
  frameIndex: number
  worldX: number
  worldY: number
}

export interface VisibleFrameWithCoverage {
  frameIndex: number
  offset: { x: number; y: number }  // Render offset (viewport-relative position)
  coverage: number                   // Coverage percentage (0.0 to 1.0)
  distance3d?: number                // 3D distance in COLMAP space (from offsetMap)
  confidence?: number                // Confidence 0.0-1.0 (from offsetMap)
  angleDiff?: number                 // Camera angle difference (from offsetMap)
  unifiedScore?: number              // Unified score: 0.4*coverage + 0.6*geometry
}

/**
 * Calculate pan angle from delta movements
 * @param panDeltaX - Pan distance in X (pixels)
 * @param panDeltaY - Pan distance in Y (pixels)
 * @returns Angle in degrees (0° = right, 90° = down, 180° = left, 270° = up)
 */
function calculatePanAngle(panDeltaX: number, panDeltaY: number): number {
  const radians = Math.atan2(panDeltaY, panDeltaX)
  return (radians * 180) / Math.PI
}

/**
 * Normalize angle difference to 0-180°
 * (we want the SHORTER angle between two directions)
 * @param angle1 - First angle in degrees
 * @param angle2 - Second angle in degrees
 * @returns Normalized difference (0-180°)
 */
function normalizeAngleDifference(angle1: number, angle2: number): number {
  let diff = Math.abs(angle1 - angle2)
  if (diff > 180) {
    diff = 360 - diff
  }
  return diff
}

/**
 * Check if camera angle aligns with pan direction
 * @param panDeltaX - Pan delta in X
 * @param panDeltaY - Pan delta in Y
 * @param cameraAngleDiff - Camera angle from offsetMap
 * @param maxAngleDifference - Threshold in degrees (default: 90°)
 * @returns true if aligned, false if perpendicular/opposite
 */
function isAngleAlignedWithPan(
  panDeltaX: number,
  panDeltaY: number,
  cameraAngleDiff: number,
  maxAngleDifference: number = 90
): boolean {
  // If no pan movement detected, accept all angles
  if (Math.abs(panDeltaX) < 5 && Math.abs(panDeltaY) < 5) {
    return true
  }

  const panAngle = calculatePanAngle(panDeltaX, panDeltaY)
  const angleDiff = normalizeAngleDifference(panAngle, cameraAngleDiff)

  return angleDiff <= maxAngleDifference
}

/**
 * Calculate geometry score from offset data
 * Uses: confidence (shared points), pointCount, and dz (depth change)
 * @param offsetData - Offset data from offsetMap
 * @returns Geometry score 0.0-1.0 (higher is better)
 */
function calculateGeometryScoreFromOffset(offsetData: any): number {
  if (!offsetData) return 0

  // Confidence score: how consistent the offset is
  // 0.5 (poor) → 1.0 (excellent)
  const confidenceScore = Math.max(0, Math.min(1, (offsetData.confidence ?? 0.5) * 2 - 1))

  // Point count score: more shared points = more reliable
  // Normalize: 0 points = 0, 500+ points = 1.0
  const pointCount = offsetData.pointCount ?? 0
  const pointScore = Math.min(1, pointCount / 500)

  // DZ score: small depth changes are good (close to 0), large changes are bad
  // |dz| = 0.0 → score 1.0, |dz| > 0.3 → score 0.0
  const dzScore = Math.max(0, 1.0 - Math.abs(offsetData.dz ?? 0) / 0.3)

  // Blend: 50% confidence, 30% points, 20% depth
  return confidenceScore * 0.5 + pointScore * 0.3 + dzScore * 0.2
}

/**
 * Calculate which frames are visible around a reference point based on proximity.
 *
 * Holy Red Dot Paradigm: Finds frames within a radius of the reference point
 * (typically the world position under the red dot at screen center).
 * Frames are positioned relative to the reference point for proper navigation context.
 *
 * @param referenceWorldPosition - Reference point in world space (e.g., world under red dot)
 * @param viewportSize - Viewport dimensions (used for radius calculation)
 * @param frameWorldPositions - Positions of all frames in world space
 * @param frameSize - Size of each frame {width, height}
 * @param options - Optional configuration
 * @returns Array of visible frames with proximity information
 */
export function calculateVisibleFramesFromViewport(
  referenceWorldPosition: { x: number; y: number },
  viewportSize: { width: number; height: number },
  frameWorldPositions: FrameWorldPosition[],
  frameSize: { width: number; height: number },
  options?: {
    coverageThreshold?: number  // Minimum coverage to include (default: 0.01 = 1%)
    currentCameraIndex?: number // Current frame index for connectivity filter (default: -1 = no filter)
    offsetMap?: OffsetMap       // Offset map for checking frame connectivity (DEPRECATED - use connectivityGraph instead)
    connectivityGraph?: Map<number, Set<number>> // Cached connectivity graph (preferred over offsetMap)
    maxRadius?: number          // Maximum radius from reference point (default: viewport diagonal)
    maxFrames?: number          // Maximum number of frames to return (default: 50)
  }
): VisibleFrameWithCoverage[] {
  const coverageThreshold = options?.coverageThreshold ?? 0.01
  const currentCameraIndex = options?.currentCameraIndex ?? -1
  const connectivityGraph = options?.connectivityGraph ?? null
  const maxRadius = options?.maxRadius ?? Math.sqrt(viewportSize.width ** 2 + viewportSize.height ** 2) / 2
  const maxFrames = options?.maxFrames ?? 50

  const visibleFrames: VisibleFrameWithCoverage[] = []

  // Check each frame for proximity to reference point
  for (const framePos of frameWorldPositions) {
    // Calculate distance from frame center to reference point
    const dx = framePos.worldX - referenceWorldPosition.x
    const dy = framePos.worldY - referenceWorldPosition.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    // Skip if too far from reference point
    if (distance > maxRadius) {
      continue
    }

    // Calculate proximity score (inverse of distance, normalized)
    // Closer frames get higher scores
    const proximityScore = Math.max(0, 1 - distance / maxRadius)

    // For context frames, we use proximity instead of viewport coverage
    // This represents how "relevant" the frame is to the current navigation context
    const coverage = proximityScore

    // Skip if below threshold
    if (coverage < coverageThreshold) {
      continue
    }

    // Calculate render offset (where to position frame relative to reference point)
    // Frame world position - Reference world position = Reference-relative position
    const offsetX = framePos.worldX - referenceWorldPosition.x
    const offsetY = framePos.worldY - referenceWorldPosition.y

    // Get geometry metrics from offset map
    let geometryScore = 0
    let unifiedScore = coverage  // Default to proximity if no geometry data

    if (options?.offsetMap && currentCameraIndex !== -1) {
      const offsetKey = `${currentCameraIndex}_${framePos.frameIndex}`
      const offsetData = options.offsetMap.sequential?.[offsetKey]

      if (offsetData) {
        // Calculate geometry score from available offset data
        geometryScore = calculateGeometryScoreFromOffset(offsetData)

        // Unified score: 40% proximity + 60% geometry
        unifiedScore = coverage * 0.4 + geometryScore * 0.6
      }
    }

    visibleFrames.push({
      frameIndex: framePos.frameIndex,
      offset: { x: offsetX, y: offsetY },
      coverage,
      distance3d: undefined,
      confidence: undefined,
      angleDiff: undefined,
      unifiedScore
    })
  }

  // 🔗 CONNECTIVITY FILTER - Only select from connected neighbors
  if (currentCameraIndex !== -1 && connectivityGraph && connectivityGraph.size > 0) {
    const connectedFrames = visibleFrames.filter(f =>
      isFrameConnected(currentCameraIndex, f.frameIndex, connectivityGraph)
    )

    // Log connectivity filtering only on changes
    if (connectedFrames.length === 0) {
      // No connected frames - use fallback
    } else if (connectedFrames.length < visibleFrames.length) {
      // Return only connected frames (non-empty list guaranteed by the condition above)
      // Sort by unified score descending (best scoring first)
      connectedFrames.sort((a, b) => (b.unifiedScore ?? 0) - (a.unifiedScore ?? 0))
      return connectedFrames.slice(0, maxFrames)
    }
  } else if (currentCameraIndex !== -1 && (!connectivityGraph || connectivityGraph.size === 0)) {
    console.warn(`⚠️  [Connectivity Filter] Graph is empty or null, skipping connectivity filter`)
  }

  // Sort by unified score descending (most valuable first)
  // Unified score = 40% proximity + 60% geometry quality
  visibleFrames.sort((a, b) => (b.unifiedScore ?? 0) - (a.unifiedScore ?? 0))

  // Limit to max frames
  return visibleFrames.slice(0, maxFrames)
}

/**
 * Calculate frames needed to cover the entire viewport area.
 *
 * This function finds frames that intersect the viewport bounds and calculates their
 * positions using cumulative offsets, allowing coverage of non-directly connected frames.
 * Used for zoomed-out views where we want to fill the entire viewport with context frames.
 *
 * @param currentFrame - Reference frame index
 * @param viewportBounds - Viewport bounds in world space {minX, maxX, minY, maxY}
 * @param frameWorldPositions - Positions of all frames in world space
 * @param frameSize - Size of each frame {width, height}
 * @param offsetMap - Offset map for calculating cumulative offsets
 * @param options - Optional configuration
 * @returns Array of frames that cover the viewport with their calculated positions
 */
export function calculateViewportCoverageFrames(
  currentFrame: number,
  viewportBounds: { minX: number; maxX: number; minY: number; maxY: number },
  frameWorldPositions: FrameWorldPosition[],
  frameSize: { width: number; height: number },
  offsetMap: OffsetMap | null,
  options?: {
    maxFrames?: number          // Maximum number of frames to return (default: 100)
    overlapThreshold?: number   // Minimum overlap % with viewport (default: 0.01 = 1%)
  }
): VisibleFrameWithCoverage[] {
  const maxFrames = options?.maxFrames ?? 100
  const overlapThreshold = options?.overlapThreshold ?? 0.01

  const coverageFrames: VisibleFrameWithCoverage[] = []

  // Find frames that intersect the viewport
  for (const framePos of frameWorldPositions) {
    // Calculate frame bounds in world space
    const halfWidth = frameSize.width / 2
    const halfHeight = frameSize.height / 2
    const frameMinX = framePos.worldX - halfWidth
    const frameMaxX = framePos.worldX + halfWidth
    const frameMinY = framePos.worldY - halfHeight
    const frameMaxY = framePos.worldY + halfHeight

    // Check if frame intersects viewport
    const intersects = !(frameMaxX < viewportBounds.minX ||
                        frameMinX > viewportBounds.maxX ||
                        frameMaxY < viewportBounds.minY ||
                        frameMinY > viewportBounds.maxY)

    if (!intersects) {
      continue
    }

    // Calculate overlap percentage
    const overlapWidth = Math.max(0, Math.min(frameMaxX, viewportBounds.maxX) - Math.max(frameMinX, viewportBounds.minX))
    const overlapHeight = Math.max(0, Math.min(frameMaxY, viewportBounds.maxY) - Math.max(frameMinY, viewportBounds.minY))
    const overlapArea = overlapWidth * overlapHeight
    const viewportArea = (viewportBounds.maxX - viewportBounds.minX) * (viewportBounds.maxY - viewportBounds.minY)
    const coverage = viewportArea > 0 ? overlapArea / viewportArea : 0

    // Skip if overlap is too small
    if (coverage < overlapThreshold) {
      continue
    }

    // Calculate position relative to current frame using cumulative offset
    const cumulativeOffset = getCumulativeOffset(currentFrame, framePos.frameIndex, offsetMap)

    if (!cumulativeOffset) {
      // No path available - skip this frame
      continue
    }

    coverageFrames.push({
      frameIndex: framePos.frameIndex,
      offset: cumulativeOffset,  // Position relative to current frame
      coverage,
      distance3d: undefined,
      confidence: undefined,
      angleDiff: undefined,
      unifiedScore: coverage  // Use coverage as unified score for viewport filling
    })
  }

  // Sort by coverage descending (best coverage first)
  coverageFrames.sort((a, b) => (b.coverage ?? 0) - (a.coverage ?? 0))

  // Limit to max frames
  return coverageFrames.slice(0, maxFrames)
}

